import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Download, Loader2 } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MeuQRCode = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const pageUrl = user ? `${window.location.origin}/empresa/${user.id}` : '';

  useEffect(() => {
    const userData = localStorage.getItem('lembrou_user');
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      navigate('/auth');
    }
  }, [navigate]);

  const handlePrint = () => {
    window.print();
  };

  // Baixar PDF A5 (2 por página, para display de balcão)
  const handleDownloadA5 = async () => {
    if (!user) return;
    setDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/empresa/${user.id}/qrcode-pdf?formato=a5`);
      if (!response.ok) throw new Error('Erro ao gerar PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `QRCode-A5-${user.nome_empresa.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
    } finally {
      setDownloading(false);
    }
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center">Carregando...</div>;
  }

  return (
    <>
      {/* Header com ações (não imprime) */}
      <div className="no-print bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="rounded-xl"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </Button>
          <div className="flex gap-3">
            <Button
              onClick={handleDownloadA5}
              variant="outline"
              className="rounded-xl"
              disabled={downloading}
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {downloading ? 'Gerando...' : 'Baixar A5'}
            </Button>
            <Button
              onClick={handlePrint}
              className="rounded-xl bg-primary hover:bg-primary-hover"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </div>
        </div>
      </div>

      {/* Área de impressão - Layout profissional */}
      <div className="print-page bg-gray-100 min-h-screen py-8 print:py-0 print:bg-white flex items-center justify-center">
        <div className="print-content bg-white w-[380px] print:w-full max-w-[180mm] shadow-xl print:shadow-none relative overflow-hidden"
          style={{ minHeight: '540px' }}
        >
          {/* Decoração canto superior esquerdo */}
          <div className="absolute top-5 left-5">
            <svg width="36" height="36" viewBox="0 0 40 40">
              <rect x="0" y="0" width="14" height="14" rx="3" fill="#EF4444"/>
              <rect x="18" y="0" width="14" height="14" rx="3" fill="#EF4444" fillOpacity="0.5"/>
              <rect x="0" y="18" width="14" height="14" rx="3" fill="#EF4444"/>
              <rect x="18" y="18" width="14" height="14" rx="3" fill="#EF4444"/>
            </svg>
          </div>

          {/* Conteúdo */}
          <div className="px-6 pt-16 pb-6 print:px-10 print:pt-20 print:pb-8">
            
            {/* Header: Logo Lembrou? */}
            <div className="text-center mb-5">
              <div className="flex items-center justify-center gap-2 mb-4">
                {/* Ícone calendário */}
                <svg width="32" height="32" viewBox="0 0 40 40">
                  <rect x="2" y="6" width="36" height="32" rx="4" fill="#EF4444"/>
                  <rect x="8" y="2" width="4" height="8" rx="2" fill="#EF4444"/>
                  <rect x="28" y="2" width="4" height="8" rx="2" fill="#EF4444"/>
                  <rect x="2" y="6" width="36" height="10" fill="#DC2626"/>
                  <rect x="8" y="20" width="5" height="5" rx="1" fill="white"/>
                  <rect x="17.5" y="20" width="5" height="5" rx="1" fill="white"/>
                  <rect x="27" y="20" width="5" height="5" rx="1" fill="white"/>
                  <rect x="8" y="28" width="5" height="5" rx="1" fill="white"/>
                  <rect x="17.5" y="28" width="5" height="5" rx="1" fill="white"/>
                  <rect x="27" y="28" width="5" height="5" rx="1" fill="white"/>
                </svg>
                <span className="text-3xl font-bold text-primary tracking-tight">lembrou?</span>
              </div>
              
              {/* Nome da empresa */}
              <h1 className="text-2xl font-bold text-gray-900 mb-0.5">
                {user.nome_empresa}
              </h1>
              <p className="text-base text-gray-400">
                {user.tipo_profissao}
              </p>
            </div>

            {/* Call to action box com decoração de calendário */}
            <div className="relative bg-red-50 border-2 border-red-200 rounded-2xl p-4 mb-5">
              {/* Tabs de calendário no topo */}
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 flex gap-1.5">
                <div className="w-2.5 h-5 bg-primary rounded-b-md"></div>
                <div className="w-2.5 h-5 bg-primary rounded-b-md"></div>
                <div className="w-2.5 h-5 bg-primary rounded-b-md"></div>
                <div className="w-2.5 h-5 bg-primary rounded-b-md"></div>
              </div>
              
              <h2 className="text-lg font-bold text-primary text-center mt-1 mb-1.5">
                A gente lembra para você!
              </h2>
              <p className="text-center text-gray-700 text-sm leading-relaxed">
                <strong>Escaneie o QR Code</strong> e nunca mais<br/>
                perca um prazo importante!<br/>
                <strong>Receba lembretes automáticos</strong><br/>
                antes do vencimento.
              </p>
            </div>

            {/* QR Code + Instruções lado a lado */}
            <div className="flex gap-4 mb-5">
              {/* QR Code */}
              <div className="flex-shrink-0">
                <QRCodeCanvas 
                  id="company-qrcode"
                  value={pageUrl}
                  size={130}
                  level="H"
                  includeMargin={false}
                />
              </div>

              {/* Como funciona */}
              <div className="flex-1">
                <h3 className="text-base font-bold text-gray-900 mb-2.5">
                  Como funciona?
                </h3>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2.5">
                    <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xs">1</span>
                    </div>
                    <div>
                      <p className="font-semibold text-primary text-sm leading-none">Escaneie</p>
                      <p className="text-xs text-gray-500 mt-0.5">Use a câmera do seu celular</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xs">2</span>
                    </div>
                    <div>
                      <p className="font-semibold text-primary text-sm leading-none">Cadastre</p>
                      <p className="text-xs text-gray-500 mt-0.5">Seus dados e lembretes</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xs">3</span>
                    </div>
                    <div>
                      <p className="font-semibold text-primary text-sm leading-none">Relaxe</p>
                      <p className="text-xs text-gray-500 mt-0.5">Receba alertas automáticos</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefícios */}
            <div className="border-t-2 border-gray-200 pt-4 flex items-start gap-4">
              <div className="flex-shrink-0">
                <p className="text-base font-bold text-primary leading-tight">
                  O que você<br/>ganha?
                </p>
              </div>
              <div className="flex-1 text-xs text-gray-600 space-y-0.5">
                <p><strong className="text-primary">Lembretes</strong> automáticos por E-mail e WhatsApp</p>
                <p><strong className="text-primary">Gratuito</strong> para você, cliente</p>
                <p><strong className="text-primary">Organização</strong> de todos os prazos</p>
                <p><strong className="text-primary">Rápido</strong>, cadastro em 1 minuto</p>
              </div>
              
              {/* Decoração canto inferior direito */}
              <div className="flex-shrink-0 self-end">
                <svg width="28" height="28" viewBox="0 0 40 40">
                  <rect x="12" y="12" width="10" height="10" rx="2" fill="#EF4444" fillOpacity="0.5"/>
                  <rect x="26" y="12" width="10" height="10" rx="2" fill="#EF4444"/>
                  <rect x="12" y="26" width="10" height="10" rx="2" fill="#EF4444"/>
                  <rect x="26" y="26" width="10" height="10" rx="2" fill="#EF4444"/>
                </svg>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Estilos de impressão */}
      <style>{`
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          .no-print {
            display: none !important;
          }
          
          .print-page {
            padding: 0 !important;
            margin: 0 !important;
            background: white !important;
            min-height: 100vh !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          .print-content {
            width: 100% !important;
            max-width: 180mm !important;
            min-height: auto !important;
            margin: 0 auto !important;
            box-shadow: none !important;
            transform: scale(1.3);
            transform-origin: center center;
          }
          
          @page {
            size: A4 portrait;
            margin: 15mm;
          }
        }
      `}</style>
    </>
  );
};

export default MeuQRCode;
